/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/**
 * ---------------------------------------------------------------------------
 * SMITH MACHINE PDP — GALLERY
 * ---------------------------------------------------------------------------
 *
 * Thumbnail switching, prev/next arrows, swipe and drag for the buy-box gallery.
 *
 * Variant-to-media synchronization is handled by the client-side variant
 * switcher below. Gallery controls are delegated so they survive theme-editor
 * section reloads.
 */
(function () {
  'use strict';

  var STAGE = '[data-sm-gallery-stage]';

  function imagesIn(stage) {
    return Array.prototype.slice.call(stage.querySelectorAll('[data-sm-main-image]'));
  }

  function thumbsIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-sm-thumb]'));
  }

  function show(stage, index) {
    var images = imagesIn(stage);
    if (!images.length) return;

    var next = ((index % images.length) + images.length) % images.length;

    images.forEach(function (img, i) {
      img.hidden = i !== next;
    });

    var section = stage.closest('.shopify-section') || document;

    thumbsIn(section).forEach(function (thumb) {
      thumb.classList.toggle('is-active', Number(thumb.dataset.smThumb) === next);
    });

    var counter = stage.querySelector('[data-sm-counter]');
    if (counter) counter.textContent = (next + 1) + ' / ' + images.length;

    stage.dataset.smCurrent = String(next);
  }

  function currentIndex(stage) {
    return Number(stage.dataset.smCurrent || 0);
  }

  function stageForEvent(target) {
    var section = target.closest('.shopify-section');
    return section ? section.querySelector(STAGE) : null;
  }

  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-sm-thumb]');
    if (thumb) {
      var stage = stageForEvent(thumb);
      if (stage) show(stage, Number(thumb.dataset.smThumb));
      return;
    }

    var nav = event.target.closest('[data-sm-gal-nav]');
    if (nav) {
      var navStage = stageForEvent(nav);
      if (navStage) {
        var delta = nav.dataset.smGalNav === 'prev' ? -1 : 1;
        show(navStage, currentIndex(navStage) + delta);
      }
    }
  });

  /* Swipe and drag — bound per stage, scoped so arrow keys are never hijacked
     page-wide the way the previous implementation did. */
  function bindDrag(stage) {
    if (stage.dataset.smDragBound === 'true') return;
    stage.dataset.smDragBound = 'true';

    var startX = null;

    stage.addEventListener('touchstart', function (e) {
      startX = e.changedTouches[0].clientX;
    }, { passive: true });

    stage.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) show(stage, currentIndex(stage) + (dx < 0 ? 1 : -1));
      startX = null;
    }, { passive: true });

    stage.addEventListener('mousedown', function (e) {
      startX = e.clientX;
    });

    stage.addEventListener('mouseup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 80) show(stage, currentIndex(stage) + (dx < 0 ? 1 : -1));
      startX = null;
    });

    stage.addEventListener('mouseleave', function () {
      startX = null;
    });
  }

  function injectNav(stage) {
    if (stage.querySelector('[data-sm-gal-nav]')) return;
    if (imagesIn(stage).length < 2) return;

    ['prev', 'next'].forEach(function (dir) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sm-gal-nav sm-gal-nav--' + dir;
      button.setAttribute('data-sm-gal-nav', dir);
      button.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
      button.innerHTML = dir === 'prev'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';
      stage.appendChild(button);
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll(STAGE).forEach(function (stage) {
      injectNav(stage);
      bindDrag(stage);
      if (!stage.dataset.smCurrent) stage.dataset.smCurrent = '0';
    });
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  if (document.readyState !== 'loading') init(document);

  /* Re-init after a variant change swaps the section markup. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.documentElement.addEventListener('product:rerender', function () { init(document); });
})();

/**
 * ---------------------------------------------------------------------------
 * SMITH MACHINE PDP — DETAIL TABS
 * ---------------------------------------------------------------------------
 *
 * Delegated so it survives section re-renders. Implements the ARIA tab pattern
 * (roving tabindex + arrow keys), which the previous implementation lacked.
 */
(function () {
  'use strict';

  function panelsFor(nav) {
    var section = nav.closest('.shopify-section') || document;
    return Array.prototype.slice.call(section.querySelectorAll('[data-sm-panel]'));
  }

  function tabsFor(nav) {
    return Array.prototype.slice.call(nav.querySelectorAll('[data-sm-tab]'));
  }

  function activate(nav, key, focusTab) {
    tabsFor(nav).forEach(function (tab) {
      var selected = tab.dataset.smTab === key;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (selected && focusTab) tab.focus();
    });

    panelsFor(nav).forEach(function (panel) {
      panel.hidden = panel.dataset.smPanel !== key;
    });
  }

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-sm-tab]');
    if (!tab) return;

    var nav = tab.closest('[data-sm-tabs]');
    if (nav) activate(nav, tab.dataset.smTab, false);
  });

  /* Arrow keys move between tabs — scoped to the tablist, never document-wide. */
  document.addEventListener('keydown', function (event) {
    var tab = event.target.closest('[data-sm-tab]');
    if (!tab) return;

    var nav = tab.closest('[data-sm-tabs]');
    if (!nav) return;

    var tabs = tabsFor(nav);
    var index = tabs.indexOf(tab);
    var next = null;

    switch (event.key) {
      case 'ArrowRight': next = tabs[(index + 1) % tabs.length]; break;
      case 'ArrowLeft': next = tabs[(index - 1 + tabs.length) % tabs.length]; break;
      case 'Home': next = tabs[0]; break;
      case 'End': next = tabs[tabs.length - 1]; break;
      default: return;
    }

    event.preventDefault();
    activate(nav, next.dataset.smTab, true);
  });
})();

/**
 * ---------------------------------------------------------------------------
 * SMITH MACHINE PDP — SAVINGS CALCULATOR
 * ---------------------------------------------------------------------------
 *
 * Formula (unchanged from the original page):
 *   yearly   = (cost * paymentsPerYear + admin) * people
 *   totalGym = yearly * years
 *   savings  = totalGym - homePrice
 *   breakeven = max(1, round((homePrice / yearly) * 12))
 *
 * homePrice comes from the section's data-home-price, which Liquid renders from
 * the live variant. It also updates on variant:changed so the comparison stays
 * correct when the shopper switches variants — the old build always compared
 * against a hardcoded 1599 regardless of selection.
 */
(function () {
  'use strict';

  function money(value) {
    return '$' + Math.round(value).toLocaleString('en-CA');
  }

  function setup(wrap) {
    if (!wrap || wrap.dataset.smCalcBound === 'true') return;
    wrap.dataset.smCalcBound = 'true';

    var costEl = wrap.querySelector('[data-sm-cost]');
    var adminEl = wrap.querySelector('[data-sm-admin]');
    var peopleEl = wrap.querySelector('[data-sm-people]');
    var yearsEl = wrap.querySelector('[data-sm-years]');
    var chartEl = wrap.querySelector('[data-sm-chart]');
    if (!costEl || !chartEl) return;

    var state = {
      cost: parseInt(costEl.value, 10) || 0,
      admin: adminEl ? parseInt(adminEl.value, 10) || 0 : 0,
      people: parseInt((peopleEl && peopleEl.querySelector('.is-active') || {}).dataset ? peopleEl.querySelector('.is-active').dataset.people : 1, 10) || 1,
      years: parseInt((yearsEl && yearsEl.querySelector('.is-active') || {}).dataset ? yearsEl.querySelector('.is-active').dataset.years : 5, 10) || 5
    };

    function homePrice() {
      return parseFloat(wrap.dataset.homePrice) || 0;
    }

    function payPerYear() {
      return parseInt(wrap.dataset.payPerYear, 10) || 26;
    }

    function text(selector, value) {
      var el = wrap.querySelector(selector);
      if (el) el.textContent = value;
    }

    function recalc() {
      var home = homePrice();
      var yearly = (state.cost * payPerYear() + state.admin) * state.people;
      var totalGym = yearly * state.years;
      var savings = totalGym - home;
      var breakeven = yearly > 0 ? Math.max(1, Math.round((home / yearly) * 12)) : 0;

      text('[data-sm-cost-val]', '$' + state.cost);
      text('[data-sm-admin-val]', '$' + state.admin);
      text('[data-sm-people-val]', String(state.people));
      text('[data-sm-years-label]', state.years + ' years');

      var recap = (state.people === 1 ? '1 person' : state.people + ' people') +
        ' paying $' + state.cost + ' biweekly' +
        (state.admin > 0 ? ' + $' + state.admin + ' admin/yr' : '');
      text('[data-sm-recap]', recap);

      text('[data-sm-gym-total]', money(totalGym));
      text('[data-sm-home-total]', money(home));
      text('[data-sm-savings]', money(savings));
      text('[data-sm-breakeven]', breakeven + (breakeven === 1 ? ' month' : ' months'));

      var max = Math.max(totalGym, home) || 1;
      var html = '';

      chartEl.classList.toggle('is-dense', state.years > 5);

      for (var y = 1; y <= state.years; y++) {
        var gym = yearly * y;
        var saved = Math.max(0, gym - home);

        html += '<div class="sm-calc-col">';
        html += saved > 0
          ? '<div class="sm-calc-col-savings">SAVED<span class="sm-ccs-amt">' + money(saved) + '</span></div>'
          : '<div class="sm-calc-col-savings" style="visibility:hidden">.</div>';
        html += '<div class="sm-calc-stack">';
        html += '<div class="sm-calc-bar is-gym" style="height:' + ((gym / max) * 100) + '%"><span class="sm-calc-bar-val">' + money(gym) + '</span></div>';
        html += '<div class="sm-calc-bar is-home" style="height:' + ((home / max) * 100) + '%"><span class="sm-calc-bar-val">' + money(home) + '</span></div>';
        html += '</div>';
        html += '<div class="sm-calc-col-label">YR ' + y + '</div>';
        html += '</div>';
      }

      chartEl.innerHTML = html;
    }

    costEl.addEventListener('input', function () {
      state.cost = parseInt(costEl.value, 10) || 0;
      recalc();
    });

    if (adminEl) {
      adminEl.addEventListener('input', function () {
        state.admin = parseInt(adminEl.value, 10) || 0;
        recalc();
      });
    }

    function bindGroup(container, key, selector) {
      if (!container) return;
      container.addEventListener('click', function (event) {
        var button = event.target.closest(selector);
        if (!button) return;

        state[key] = parseInt(button.dataset[key === 'people' ? 'people' : 'years'], 10);

        container.querySelectorAll(selector).forEach(function (el) {
          var active = el === button;
          el.classList.toggle('is-active', active);
          el.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        recalc();
      });
    }

    bindGroup(peopleEl, 'people', '.sm-calc-person');
    bindGroup(yearsEl, 'years', '.sm-calc-year');

    /* Keep the comparison price in step with the selected variant. */
    document.addEventListener('variant:changed', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant || !variant.price) return;
      wrap.dataset.homePrice = String(Math.floor(variant.price / 100));
      recalc();
    });

    recalc();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-sm-calc]').forEach(setup);
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  if (document.readyState !== 'loading') init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();

/**
 * ---------------------------------------------------------------------------
 * SMITH MACHINE PDP — VARIANT SWITCHER + STICKY ADD TO CART
 * ---------------------------------------------------------------------------
 *
 * Client-side variant switching, matching the original page's behaviour: when
 * the shopper changes an option, price, gallery image, monthly figure, the
 * selected-value labels, the CTA and the sticky bar all update instantly with
 * no page reload. It reads the variant data map emitted by sm-buy-box.liquid
 * and drives plain radios carrying Shopify option-value IDs, so the product
 * form always submits the correct variant without Focal's server re-render.
 *
 * The sticky bar reveals when the main CTA scrolls out of view; its stack pills
 * mirror the first option both ways, and its button submits the buy-box form so
 * there is a single source of truth for the selected variant.
 */
(function () {
  'use strict';

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function scopeOf(el) {
    return el.closest('.shopify-section') ? document : document;
  }

  function setup(section) {
    var picker = section.querySelector('[data-sm-variant-picker]');
    var dataEl = section.querySelector('[data-sm-variants]');
    if (!picker || !dataEl || picker.dataset.smVariantBound === 'true') return;
    picker.dataset.smVariantBound = 'true';

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var form = document.getElementById(picker.getAttribute('form-id'));
    var priceCard = section.querySelector('.sm-price-card');
    var monthlyDivisor = priceCard ? parseInt(priceCard.dataset.smMonthlyDivisor, 10) || 18 : 18;

    /* Read the currently selected option values from the checked radios.
       Reads data-value (the exact option string) rather than label text, since
       pill labels also contain price/subtext that would break the match. */
    function selectedOptions() {
      var opts = [];
      picker.querySelectorAll('input[type=radio]:checked').forEach(function (r) {
        var pos = parseInt(r.dataset.optionPosition, 10);
        opts[pos - 1] = r.dataset.value || '';
      });
      return opts;
    }

    function findVariant(opts) {
      return variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }

    function setText(sel, value) {
      var el = section.querySelector(sel);
      if (el) el.textContent = value;
    }

    function updateGalleryImage(variant) {
      if (variant.featured_media_index === null || variant.featured_media_index === undefined) return;
      var stage = section.querySelector('[data-sm-gallery-stage]');
      if (!stage) return;
      var images = stage.querySelectorAll('[data-sm-main-image]');
      var idx = variant.featured_media_index;
      if (idx < 0 || idx >= images.length) return;
      images.forEach(function (img, i) { img.hidden = i !== idx; });
      var counter = stage.querySelector('[data-sm-counter]');
      if (counter) counter.textContent = (idx + 1) + ' / ' + images.length;
      section.querySelectorAll('[data-sm-thumb]').forEach(function (t) {
        t.classList.toggle('is-active', Number(t.dataset.smThumb) === idx);
      });
      stage.dataset.smCurrent = String(idx);
    }

    function apply(changedRadio) {
      var opts = selectedOptions();
      var variant = findVariant(opts);
      if (!variant && changedRadio) {
        var changedIndex = parseInt(changedRadio.dataset.optionPosition, 10) - 1;
        variant = variants.find(function (candidate) {
          return candidate.available && candidate.options[changedIndex] === changedRadio.dataset.value;
        }) || variants.find(function (candidate) {
          return candidate.options[changedIndex] === changedRadio.dataset.value;
        });
        if (variant) {
          variant.options.forEach(function (value, index) {
            var radio = Array.prototype.find.call(
              picker.querySelectorAll('input[data-option-position="' + (index + 1) + '"]'),
              function (input) { return input.dataset.value === value; }
            );
            if (radio) radio.checked = true;
          });
          opts = selectedOptions();
        }
      }
      if (!variant) return;

      /* Keep the real form input in step so the correct variant is submitted. */
      if (form) {
        var variantInput = form.elements.namedItem('id');
        if (variantInput) {
          variantInput.disabled = false;
          variantInput.value = String(variant.id);
        }
      }

      /* Price card */
      var parts = money(variant.price).replace('$', '').split('.');
      setText('[data-sm-price-int]', parts[0]);
      setText('[data-sm-price-cents]', '.' + (parts[1] || '00'));

      var compareEl = section.querySelector('[data-sm-compare]');
      if (compareEl) {
        compareEl.innerHTML = variant.compare_at_price > variant.price
          ? '<span class="sm-pc-compare">' + money(variant.compare_at_price) + '</span>'
          : '';
      }

      /* Monthly figure */
      var monthlyEl = section.querySelector('[data-sm-monthly]');
      if (monthlyEl) {
        monthlyEl.textContent = Math.floor((variant.price / 100) / monthlyDivisor).toLocaleString('en-CA');
      }
      var financeMonthlyEl = section.querySelector('[data-sm-finance-monthly]');
      if (financeMonthlyEl) {
        financeMonthlyEl.textContent = Math.floor((variant.price / 100) / monthlyDivisor).toLocaleString('en-CA');
      }
      var financeExactEl = section.querySelector('[data-sm-finance-exact]');
      if (financeExactEl) financeExactEl.textContent = money(Math.ceil(variant.price / monthlyDivisor));

      /* Selected-value labels */
      opts.forEach(function (val, i) {
        setText('[data-sm-selected-val="' + (i + 1) + '"]', val);
      });

      /* Main CTA */
      var cta = section.querySelector('[data-sm-cta]');
      var ctaText = section.querySelector('[data-sm-cta-text]');
      var isPreorder = !!variant.preorder;
      if (cta) {
        cta.disabled = !variant.available;
        if (ctaText) {
          var buyLabel = isPreorder && cta.dataset.preorderLabel ? cta.dataset.preorderLabel : cta.dataset.addLabel;
          ctaText.textContent = variant.available
            ? buyLabel + ' — ' + money(variant.price)
            : cta.dataset.soldoutLabel;
        }
        var ctaSub = section.querySelector('[data-sm-cta-sub]');
        if (ctaSub && cta.dataset.preorderLabel !== undefined) {
          var subText = isPreorder ? cta.dataset.preorderSubtext : cta.dataset.addSubtext;
          ctaSub.textContent = subText || '';
          ctaSub.hidden = !subText;
        }
      }

      /* Pre-order: swap stock/pickup for the ship window + terms box, and
         attach the ship window to the line item. */
      section.querySelectorAll('[data-sm-preorder-show]').forEach(function (el) { el.hidden = !isPreorder; });
      section.querySelectorAll('[data-sm-preorder-hide]').forEach(function (el) { el.hidden = isPreorder; });
      section.querySelectorAll('[data-sm-preorder-prop]').forEach(function (el) { el.disabled = !isPreorder; });

      /* Gallery image */
      updateGalleryImage(variant);

      /* Sticky bar */
      var sticky = document.querySelector('[data-sm-sticky][data-product-id="' + picker.dataset.productId + '"]');
      var stickyPrice = sticky && sticky.querySelector('[data-sm-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      var stickyCompare = sticky && sticky.querySelector('[data-sm-sticky-compare]');
      if (stickyCompare) stickyCompare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
      var stickyImg = sticky && sticky.querySelector('[data-sm-sticky-image]');
      if (stickyImg && variant.featured_image) stickyImg.src = variant.featured_image;
      var stickyAdd = sticky && sticky.querySelector('[data-sm-sticky-add]');
      if (stickyAdd) {
        stickyAdd.disabled = !variant.available;
        var stickyLabel = isPreorder && stickyAdd.dataset.preorderLabel ? stickyAdd.dataset.preorderLabel : stickyAdd.dataset.addLabel;
        stickyAdd.textContent = variant.available ? stickyLabel : stickyAdd.dataset.soldoutLabel;
      }
      /* Sticky pills reflect the first option */
      if (sticky) sticky.querySelectorAll('[data-sm-sticky-pill]').forEach(function (pill) {
        pill.classList.toggle('is-active', pill.dataset.value === opts[0]);
      });

      /* Let other components (e.g. the calculator) react. */
      if (form) {
        form.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant: variant } }));
      }
    }

    /* Radio changes drive the update. */
    picker.addEventListener('change', function (e) {
      if (e.target.matches('input[type=radio]')) apply(e.target);
    });

    /* Sticky pills select the matching first-option radio. */
    var matchingSticky = document.querySelector('[data-sm-sticky][data-product-id="' + picker.dataset.productId + '"]');
    if (matchingSticky) matchingSticky.querySelectorAll('[data-sm-sticky-pill]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        var pos = pill.dataset.optionPosition;
        var value = pill.dataset.value;
        var radios = picker.querySelectorAll('input[data-option-position="' + pos + '"]');
        radios.forEach(function (r) {
          if (r.dataset.value === value) {
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    });

    /* Prime the UI to the initial selection. */
    apply();
  }

  /* --- Sticky bar reveal + submit --- */
  function setupSticky(bar) {
    if (!bar || bar.dataset.smStickyBound === 'true') return;
    bar.dataset.smStickyBound = 'true';

    var target = document.querySelector(bar.dataset.watch || '.sm-cta-primary');

    if (target && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          /* Show whenever the buy-box button is off-screen — below the fold on
             load as well as after scrolling past it. */
          var show = !entry.isIntersecting;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      }, { threshold: 0 });
      observer.observe(target);
    }

    var addBtn = bar.querySelector('[data-sm-sticky-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var form = (target && target.closest('form')) || document.querySelector('form[is="product-form"]');
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    }
  }

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('.shopify-section').forEach(function (s) {
      if (s.querySelector('[data-sm-variant-picker]')) setup(s);
    });
    if (root.querySelector && root.querySelector('[data-sm-variant-picker]') && root !== document) setup(root);
    root.querySelectorAll('[data-sm-sticky]').forEach(setupSticky);
  }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  if (document.readyState !== 'loading') init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();

/**
 * ---------------------------------------------------------------------------
 * HOMEPAGE — ATHLETES UGC VIDEO MODAL
 * ---------------------------------------------------------------------------
 *
 * Delegated so it survives section re-renders in the theme editor. Opens a
 * vertical video (Shopify-hosted <video> or an embed <iframe>) in a modal,
 * with focus handling and Escape-to-close.
 */
(function () {
  'use strict';

  var lastTrigger = null;

  function stageFor(section) {
    return section.querySelector('[data-sm-ath-stage]');
  }

  function clearStage(stage) {
    Array.prototype.slice.call(stage.querySelectorAll('video, iframe')).forEach(function (el) {
      el.remove();
    });
  }

  function open(button) {
    var section = button.closest('.shopify-section') || document;
    var modal = section.querySelector('[data-sm-ath-modal]');
    var stage = stageFor(section);
    if (!modal || !stage) return;

    clearStage(stage);

    var videoUrl = button.dataset.video;
    var embedUrl = button.dataset.embed;

    if (videoUrl) {
      var video = document.createElement('video');
      video.src = videoUrl;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      stage.appendChild(video);
    } else if (embedUrl) {
      var iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.setAttribute('allowfullscreen', '');
      stage.appendChild(iframe);
    } else {
      /* Nothing to play yet (placeholder state) — do not open an empty modal. */
      return;
    }

    lastTrigger = button;
    modal.classList.add('is-open');

    var closeBtn = modal.querySelector('[data-sm-ath-close]');
    if (closeBtn) closeBtn.focus();
  }

  function close(modal) {
    modal.classList.remove('is-open');
    var stage = modal.querySelector('[data-sm-ath-stage]');
    if (stage) clearStage(stage);
    if (lastTrigger) { lastTrigger.focus(); lastTrigger = null; }
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-sm-ath-open]');
    if (opener) { open(opener); return; }

    var closer = event.target.closest('[data-sm-ath-close]');
    if (closer) {
      var modal = closer.closest('[data-sm-ath-modal]');
      if (modal) close(modal);
      return;
    }

    /* Click on the backdrop (the modal element itself, not its inner panel). */
    var modalBackdrop = event.target.closest('[data-sm-ath-modal]');
    if (modalBackdrop && event.target === modalBackdrop) close(modalBackdrop);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openModal = document.querySelector('[data-sm-ath-modal].is-open');
    if (openModal) close(openModal);
  });
})();

/* ---------------------------------------------------------------------------
 * PDP — VERIFIED CUSTOMER VIDEOS (buy-box card)
 * ---------------------------------------------------------------------------
 *
 * A compact strip of vertical thumbnails in the buy-box column. Arrows appear
 * only when the clips overflow and scroll the track by roughly one page. Tapping
 * a thumbnail opens the clip (hosted <video> or embed <iframe>) in a modal.
 * Delegated so it survives theme-editor section reloads.
 */
(function () {
  'use strict';

  function setup(card) {
    if (card.dataset.smVvReady) return;
    card.dataset.smVvReady = '1';
    var track = card.querySelector('[data-sm-vv-track]');
    var prev = card.querySelector('[data-sm-vv-prev]');
    var next = card.querySelector('[data-sm-vv-next]');
    if (!track) return;

    function overflowing() { return track.scrollWidth - track.clientWidth > 4; }
    function refresh() {
      var over = overflowing();
      if (prev) prev.hidden = !over || track.scrollLeft <= 2;
      if (next) next.hidden = !over || track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -track.clientWidth * 0.9, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: track.clientWidth * 0.9, behavior: 'smooth' }); });
    track.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    refresh();
  }

  function openModal(btn) {
    var card = btn.closest('[data-sm-vv]');
    var modal = card && card.querySelector('[data-sm-vv-modal]');
    var stage = modal && modal.querySelector('[data-sm-vv-stage]');
    if (!stage) return;
    var video = btn.getAttribute('data-video');
    var embed = btn.getAttribute('data-embed');
    if (video) {
      stage.innerHTML = '<video src="' + video + '" controls autoplay playsinline></video>';
    } else if (embed) {
      var src = embed;
      if (embed.indexOf('youtu.be/') > -1) {
        var id1 = embed.split('youtu.be/')[1].split('?')[0];
        src = 'https://www.youtube.com/embed/' + id1 + '?autoplay=1&playsinline=1';
      } else if (embed.indexOf('watch?v=') > -1) {
        var id2 = embed.split('v=')[1].split('&')[0];
        src = 'https://www.youtube.com/embed/' + id2 + '?autoplay=1&playsinline=1';
      }
      stage.innerHTML = '<iframe src="' + src + '" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>';
    } else { return; }
    /* Portal the modal to <body> so it escapes any isolated stacking context
       (the coverflow/card create one) and always sits above the sticky header. */
    if (modal.parentNode !== document.body) {
      modal._smHome = modal._smHome || modal.parentNode;
      document.body.appendChild(modal);
    }
    modal.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeModal(modal) {
    modal.classList.remove('is-open');
    var stage = modal.querySelector('[data-sm-vv-stage]');
    if (stage) stage.innerHTML = '';
    document.documentElement.style.overflow = '';
    /* Return it to its original spot so multiple cards keep their own modal. */
    if (modal._smHome && modal.parentNode === document.body) {
      modal._smHome.appendChild(modal);
    }
  }

  document.addEventListener('click', function (event) {
    var open = event.target.closest('[data-sm-vv-open]');
    if (open) { openModal(open); return; }
    var close = event.target.closest('[data-sm-vv-close]');
    if (close) { var m1 = close.closest('[data-sm-vv-modal]'); if (m1) closeModal(m1); return; }
    var backdrop = event.target.closest('[data-sm-vv-modal]');
    if (backdrop && event.target === backdrop) closeModal(backdrop);
  });
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.querySelector('[data-sm-vv-modal].is-open');
    if (open) closeModal(open);
  });

  function init() { document.querySelectorAll('[data-sm-vv]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ---------------------------------------------------------------------------
 * PDP — VERIFIED VIDEOS COVERFLOW (centre-stage, symmetric, infinite)
 * ---------------------------------------------------------------------------
 *
 * The centre clip is enlarged and muted-autoplays; an equal number of static
 * thumbnails sit on each side (data-sides = 1 -> 1+1, 2 -> 2+2). Positions are
 * assigned by shortest signed distance from the active index so the ring wraps
 * smoothly and infinitely. Tapping a side brings it to centre; tapping the
 * centre opens the modal (delegated data-sm-vv-open handler).
 */
(function () {
  'use strict';

  function setup(cf) {
    if (cf.dataset.smCfReady) return;
    cf.dataset.smCfReady = '1';
    var stage = cf.querySelector('[data-sm-cf-stage]');
    var slides = Array.prototype.slice.call(cf.querySelectorAll('[data-sm-cf-slide]'));
    var prev = cf.querySelector('[data-sm-cf-prev]');
    var next = cf.querySelector('[data-sm-cf-next]');
    var dotsWrap = cf.querySelector('[data-sm-cf-dots]');
    var n = slides.length;
    if (!n) return;

    var sides = parseInt(cf.getAttribute('data-sides'), 10) || 1;
    var current = 0;

    if (dotsWrap) {
      slides.forEach(function (_, i) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'sm-cf-dot' + (i === 0 ? ' is-active' : '');
        d.setAttribute('aria-label', 'Go to video ' + (i + 1));
        d.addEventListener('click', function () { go(i); });
        dotsWrap.appendChild(d);
      });
    }

    /* Load + play the centre clip (muted loop); pause the others. */
    function playCentre() {
      slides.forEach(function (s) {
        var v = s.querySelector('video[data-sm-cf-video]');
        if (!v) return;
        if (s.getAttribute('data-pos') === '0') {
          if (!v.dataset.smLoaded) {
            v.querySelectorAll('source[data-src]').forEach(function (src) { if (!src.src) src.src = src.getAttribute('data-src'); });
            v.load();
            v.dataset.smLoaded = '1';
          }
          v.muted = true;
          v.play().catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }

    function render() {
      slides.forEach(function (s, i) {
        var diff = i - current;
        if (diff > n / 2) diff -= n;
        if (diff < -n / 2) diff += n;
        if (Math.abs(diff) <= sides) s.setAttribute('data-pos', String(diff));
        else s.setAttribute('data-pos', 'hide');
      });
      if (dotsWrap) {
        dotsWrap.querySelectorAll('.sm-cf-dot').forEach(function (d, i) {
          d.classList.toggle('is-active', i === current);
        });
      }
      playCentre();
    }

    function go(i) { current = ((i % n) + n) % n; render(); }

    if (prev) prev.addEventListener('click', function () { go(current - 1); });
    if (next) next.addEventListener('click', function () { go(current + 1); });

    slides.forEach(function (s, i) {
      s.addEventListener('click', function (e) {
        if (s.getAttribute('data-pos') !== '0') { e.preventDefault(); e.stopPropagation(); go(i); }
      }, true);
    });

    var x0 = null;
    if (stage) {
      stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) go(dx < 0 ? current + 1 : current - 1);
        x0 = null;
      }, { passive: true });
    }

    /* Pause the centre video while the carousel is scrolled off-screen. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = cf.querySelector('[data-sm-cf-slide][data-pos="0"] video[data-sm-cf-video]');
          if (!v) return;
          if (entry.isIntersecting) v.play().catch(function () {});
          else v.pause();
        });
      }, { threshold: 0.2 });
      io.observe(cf);
    }

    render();
  }

  /* Mute/unmute the centre clip. Capture-phase + stopPropagation so it does not
     trigger the slide's modal-open handler. */
  function toggleMute(muteBtn) {
    var slide = muteBtn.closest('[data-sm-cf-slide]');
    var v = slide && slide.querySelector('video[data-sm-cf-video]');
    if (!v) return;
    v.muted = !v.muted;
    muteBtn.classList.toggle('is-unmuted', !v.muted);
    if (!v.muted && v.paused) v.play().catch(function () {});
  }
  document.addEventListener('click', function (event) {
    var m = event.target.closest('[data-sm-cf-mute]');
    if (!m) return;
    event.preventDefault(); event.stopPropagation();
    toggleMute(m);
  }, true);
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var m = event.target.closest && event.target.closest('[data-sm-cf-mute]');
    if (!m) return;
    event.preventDefault(); toggleMute(m);
  });

  function init() { document.querySelectorAll('[data-sm-cf]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ---------------------------------------------------------------------------
 * HOME — HERO SLIDER
 * ---------------------------------------------------------------------------
 *
 * Desktop: crossfade between stacked slides with autoplay + arrows + dots.
 * Mobile: the same markup becomes a CSS scroll-snap track (see the section's
 * media query); here we just keep the dots in sync with the scroll position.
 * Autoplay pauses on hover/focus/touch, after manual nav, when off-screen, and
 * when prefers-reduced-motion is set. Self-initializing on [data-sm-hs].
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = function () { return window.matchMedia('(max-width: 760px)').matches; };

  function setup(root) {
    if (root.dataset.smHsReady) return;
    root.dataset.smHsReady = '1';

    var track = root.querySelector('[data-sm-hs-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-sm-hs-slide]'));
    var prev = root.querySelector('[data-sm-hs-prev]');
    var next = root.querySelector('[data-sm-hs-next]');
    var dotsWrap = root.querySelector('[data-sm-hs-dots]');
    var n = slides.length;
    if (n < 1) return;

    var current = 0;
    var timer = null;
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 6000;
    var autoplay = root.getAttribute('data-autoplay') === 'true' && n > 1 && !reduceMotion;

    if (dotsWrap) {
      slides.forEach(function (_, i) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'sm-hs-dot' + (i === 0 ? ' is-active' : '');
        d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        d.addEventListener('click', function () { userGo(i); });
        dotsWrap.appendChild(d);
      });
    }

    function renderDots() {
      if (!dotsWrap) return;
      dotsWrap.querySelectorAll('.sm-hs-dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === current);
      });
    }

    /* Desktop crossfade */
    function show(i) {
      current = ((i % n) + n) % n;
      slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === current); });
      renderDots();
    }

    /* Mobile scroll to slide */
    function scrollToSlide(i) {
      current = ((i % n) + n) % n;
      var s = slides[current];
      if (s) track.scrollTo({ left: s.offsetLeft, behavior: 'smooth' });
      renderDots();
    }

    function go(i) { if (isMobile()) scrollToSlide(i); else show(i); }
    function userGo(i) { stop(); go(i); schedule(); }

    if (prev) prev.addEventListener('click', function () { userGo(current - 1); });
    if (next) next.addEventListener('click', function () { userGo(current + 1); });

    /* Keep dots in sync when the user swipes the mobile track */
    var scrollTimer = null;
    if (track) {
      track.addEventListener('scroll', function () {
        if (!isMobile()) return;
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var mid = track.scrollLeft + track.clientWidth / 2;
          var nearest = 0, best = Infinity;
          slides.forEach(function (s, i) {
            var c = s.offsetLeft + s.offsetWidth / 2;
            var dist = Math.abs(c - mid);
            if (dist < best) { best = dist; nearest = i; }
          });
          if (nearest !== current) { current = nearest; renderDots(); }
        }, 120);
      }, { passive: true });
    }

    /* Autoplay */
    function schedule() {
      if (!autoplay) return;
      stop();
      timer = setInterval(function () { go(current + 1); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', schedule);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', schedule);
    if (track) {
      track.addEventListener('touchstart', stop, { passive: true });
      track.addEventListener('touchend', schedule, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) schedule(); else stop(); });
      }, { threshold: 0.2 });
      io.observe(root);
    } else {
      schedule();
    }

    show(0);
  }

  function init() { document.querySelectorAll('[data-sm-hs]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
